// Deploy Panorix into your own AWS account in India — sized from your userbase.
import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from './_shared';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Cloud, Copy, Download, ExternalLink, Loader2, Server, Terminal,
         ShieldCheck, CheckCircle2, XCircle, MinusCircle, BookOpen } from 'lucide-react';
import { AwsGuideDialog } from './AwsGuideDialog';
import { toast } from 'sonner';

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function AdminCloudDeploy() {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({
    users: 100, region: 'ap-south-1', name: 'panorix', fqdn: '', adminEmail: '',
    bedrock: true, bedrockModelId: 'global.anthropic.claude-opus-5', multiAz: true,
  });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState({ baseUrl: '', adminEmail: '', adminPassword: '' });
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    api.get('/aws-deploy/options').then(r => setOptions(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const size = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/aws-deploy/plan', form);
      setPlan(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not size this deployment');
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, what) => {
    navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  };

  const downloadTemplate = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/aws-deploy/template.yaml`, '_blank');
  };

  const runCheck = async () => {
    if (!check.baseUrl.trim()) { toast.error('Enter the address the stack is serving'); return; }
    setChecking(true);
    try {
      const { data } = await api.post('/aws-deploy/verify', check);
      setCheckResult(data);
      if (data.overall === 'PASS') toast.success('Every check passed');
      else toast.error(`${data.failed} check${data.failed === 1 ? '' : 's'} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run the checklist');
    } finally {
      setChecking(false);
    }
  };

  return (
    <AdminLayout title="Deploy on AWS" subtitle="Admin · Your own AWS account, in India">
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        Tell it how many people will use the platform and it sizes every AWS service for
        that load, prices it for the month, and hands you the stack to launch in your own
        account. Panorix never holds your AWS credentials — you approve the stack in your
        own console.
      </p>

      <Card className="p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="aws-guide-card">
        <BookOpen className="w-5 h-5 text-orange-700 shrink-0" />
        <div className="min-w-[260px] flex-1">
          <p className="text-sm font-semibold text-foreground">
            Never deployed anything before?
          </p>
          <p className="text-[12px] text-muted-foreground">
            The step-by-step guide takes you from creating an AWS account to signing in,
            with the AWS words explained, the costs, and what to do when something fails.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowGuide(true)}
                data-testid="aws-view-guide-btn">
          <BookOpen className="w-3.5 h-3.5 mr-1.5" />View the guide
        </Button>
        <Button size="sm" variant="outline" asChild data-testid="aws-download-guide-btn">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/aws-deploy/guide.pdf`}
             target="_blank" rel="noreferrer">
            <Download className="w-3.5 h-3.5 mr-1.5" />Download PDF
          </a>
        </Button>
      </Card>

      <AwsGuideDialog open={showGuide} onOpenChange={setShowGuide} />

      <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
        <Card className="p-5 space-y-4" data-testid="aws-deploy-form">
          <div>
            <Label className="text-xs">How many people will use it?</Label>
            <Input type="number" min={1} value={form.users}
                   onChange={e => set('users', Number(e.target.value))}
                   className="mt-1" data-testid="aws-users-input" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Named users, not concurrent sessions.
            </p>
          </div>

          <div>
            <Label className="text-xs">AWS region</Label>
            <Select value={form.region} onValueChange={v => set('region', v)}>
              <SelectTrigger className="mt-1" data-testid="aws-region-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(options?.regions || []).map(r => (
                  <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">AI engine</Label>
            <Select value={form.bedrock ? 'bedrock' : 'anthropic'}
                    onValueChange={v => set('bedrock', v === 'bedrock')}>
              <SelectTrigger className="mt-1" data-testid="aws-engine-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bedrock">Amazon Bedrock, in this AWS account</SelectItem>
                <SelectItem value="anthropic">Direct Anthropic key</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.bedrock && (
            <div>
              <Label className="text-xs">Bedrock model routing</Label>
              <Select value={form.bedrockModelId}
                      onValueChange={v => set('bedrockModelId', v)}>
                <SelectTrigger className="mt-1" data-testid="aws-bedrock-profile-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options?.bedrockProfiles || []).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                {(options?.bedrockProfiles || []).find(p => p.id === form.bedrockModelId)?.note}
              </p>
            </div>
          )}

          <div>
            <Label className="text-xs">Stack name</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)}
                   className="mt-1" data-testid="aws-name-input" />
          </div>
          <div>
            <Label className="text-xs">Hostname you will use (optional)</Label>
            <Input placeholder="panorix.yourcompany.in" value={form.fqdn}
                   onChange={e => set('fqdn', e.target.value)}
                   className="mt-1" data-testid="aws-fqdn-input" />
          </div>
          <div>
            <Label className="text-xs">First administrator's email</Label>
            <Input placeholder="admin@yourcompany.in" value={form.adminEmail}
                   onChange={e => set('adminEmail', e.target.value)}
                   className="mt-1" data-testid="aws-admin-email-input" />
          </div>

          <Button className="w-full" onClick={size} disabled={busy}
                  data-testid="aws-size-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Server className="w-4 h-4 mr-2" />}
            Size this deployment
          </Button>
        </Card>

        <div className="space-y-6 min-w-0">
          {!plan && (
            <Card className="p-5" data-testid="aws-creates-card">
              <h2 className="text-sm font-semibold text-foreground mb-3">
                What the deployment creates
              </h2>
              <ul className="space-y-1.5">
                {(options?.creates || []).map((c, i) => (
                  <li key={i} className="text-[12px] text-muted-foreground flex gap-2">
                    <Cloud className="w-3.5 h-3.5 text-sky-500 shrink-0 mt-0.5" />{c}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {plan && (
            <>
              <Card className="p-5" data-testid="aws-plan-summary">
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Footprint</p>
                    <p className="text-lg font-semibold text-foreground">{plan.band}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {plan.users} users · {plan.region_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Monthly infrastructure</p>
                    <p className="text-lg font-semibold text-foreground"
                       data-testid="aws-monthly-cost">{fmtInr(plan.monthly_inr)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      ≈ US${plan.monthly_usd.toLocaleString()} · {fmtInr(plan.per_user_inr)} per user
                    </p>
                  </div>
                  <div className="min-w-[220px]">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">AI engine</p>
                    <p className="text-[12px] text-foreground">{plan.llm}</p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">{plan.ai_cost_note}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{plan.prices_note}</p>
              </Card>

              <Card className="p-5" data-testid="aws-cost-breakdown">
                <h2 className="text-sm font-semibold text-foreground mb-3">
                  Services and monthly cost
                </h2>
                <div className="overflow-x-auto">
                <table className="w-full text-[12px] min-w-[520px]">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-1.5 font-medium">Service</th>
                      <th className="py-1.5 font-medium">Sized for your userbase</th>
                      <th className="py-1.5 font-medium text-right">Per month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.cost_lines.map((l, i) => (
                      <tr key={i} className="border-b border-border/60">
                        <td className="py-1.5 text-foreground">{l.item}</td>
                        <td className="py-1.5 text-muted-foreground">{l.detail}</td>
                        <td className="py-1.5 text-right text-foreground">{fmtInr(l.inr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </Card>

              <Card className="p-5 space-y-3" data-testid="aws-launch-card">
                <h2 className="text-sm font-semibold text-foreground">Launch it</h2>
                <p className="text-[11px] text-muted-foreground">{plan.launchNote}</p>

                <div className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-[12px] font-medium text-foreground">
                    Option 1 — one paste into AWS CloudShell (recommended)
                  </p>
                  <ol className="text-[11px] text-muted-foreground space-y-0.5 list-decimal pl-4">
                    <li>Open CloudShell in your AWS account (button below).</li>
                    <li>Copy this block, paste it into CloudShell and press Enter.</li>
                    <li>Click the link it prints — the stack form opens already filled in.</li>
                  </ol>
                  <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre"
                       data-testid="aws-cloudshell-command">{plan.cloudShellCommand}</pre>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline"
                            onClick={() => copy(plan.cloudShellCommand, 'Command')}
                            data-testid="aws-copy-command-btn">
                      <Copy className="w-3.5 h-3.5 mr-1.5" />Copy the block
                    </Button>
                    <Button size="sm" variant="outline" asChild
                            data-testid="aws-cloudshell-link">
                      <a href={plan.cloudShellUrl} target="_blank" rel="noreferrer">
                        <Terminal className="w-3.5 h-3.5 mr-1.5" />Open CloudShell
                      </a>
                    </Button>
                  </div>
                </div>

                {plan.cliCommand && (
                  <div className="rounded-lg border border-border p-3 space-y-2">
                    <p className="text-[12px] font-medium text-foreground">
                      Option 2 — create the stack entirely from the command line
                    </p>
                    <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto whitespace-pre"
                         data-testid="aws-cli-command">{plan.cliCommand}</pre>
                    <Button size="sm" variant="outline"
                            onClick={() => copy(plan.cliCommand, 'Command')}
                            data-testid="aws-copy-cli-btn">
                      <Copy className="w-3.5 h-3.5 mr-1.5" />Copy command
                    </Button>
                  </div>
                )}

                <div className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-[12px] font-medium text-foreground">
                    Option 3 — your own pipeline, or an upload by hand
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={downloadTemplate}
                            data-testid="aws-download-template-btn">
                      <Download className="w-3.5 h-3.5 mr-1.5" />Download template
                    </Button>
                    <Button size="sm" variant="outline" asChild data-testid="aws-console-link">
                      <a href={plan.consoleUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open CloudFormation
                      </a>
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => copy(JSON.stringify(plan.parameters, null, 2), 'Parameters')}
                            data-testid="aws-copy-params-btn">
                      <Copy className="w-3.5 h-3.5 mr-1.5" />Copy parameters
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-[12px] font-medium text-foreground mb-1">
                    You will be asked for
                  </p>
                  <ul className="space-y-1">
                    {plan.secretsToEnter.map((s, i) => (
                      <li key={i} className="text-[11px] text-muted-foreground">· {s}</li>
                    ))}
                  </ul>
                </div>
              </Card>
            </>
          )}

          <Card className="p-5 space-y-3" data-testid="aws-launch-checklist">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Launch checklist</h2>
              <p className="text-[11px] text-muted-foreground">
                After the stack reports CREATE_COMPLETE, point this at the address it serves.
                It checks the site answers over HTTPS, the containers are serving the API, the
                database was reached, an administrator exists — and, if you enter the
                credentials, that the first administrator can actually sign in. Nothing you
                type here is stored; a wrong password counts towards that deployment's
                sign-in lockout, so enter it exactly as you set it.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Deployed address</Label>
                <Input value={check.baseUrl} placeholder="https://panorix.yourcompany.com"
                       onChange={e => setCheck(c => ({ ...c, baseUrl: e.target.value }))}
                       className="mt-1" data-testid="aws-check-url-input" />
              </div>
              <div>
                <Label className="text-xs">Administrator email</Label>
                <Input value={check.adminEmail} placeholder="admin@yourcompany.com"
                       onChange={e => setCheck(c => ({ ...c, adminEmail: e.target.value }))}
                       className="mt-1" data-testid="aws-check-email-input" />
              </div>
              <div>
                <Label className="text-xs">Administrator password</Label>
                <Input type="password" value={check.adminPassword} placeholder="Optional"
                       onChange={e => setCheck(c => ({ ...c, adminPassword: e.target.value }))}
                       className="mt-1" data-testid="aws-check-password-input" />
              </div>
            </div>
            <Button size="sm" onClick={runCheck} disabled={checking}
                    data-testid="aws-run-check-btn">
              {checking
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Checking…</>
                : <><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Run the checklist</>}
            </Button>

            {checkResult && (
              <div className="space-y-2" data-testid="aws-check-results">
                <p className="text-[11px] text-muted-foreground">
                  {checkResult.baseUrl} · {checkResult.passed} passed
                  {checkResult.failed ? `, ${checkResult.failed} failed` : ''}
                </p>
                {checkResult.checks.map(c => (
                  <div key={c.key} className="flex items-start gap-2 text-[12px]"
                       data-testid={`aws-check-${c.key}`}>
                    {c.status === 'PASS'
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      : c.status === 'SKIPPED'
                        ? <MinusCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                        : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                    <div>
                      <p className="text-foreground">{c.label}</p>
                      <p className="text-[11px] text-muted-foreground">{c.detail}</p>
                      {c.remedy && <p className="text-[11px] text-amber-700">{c.remedy}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
